<template>
  <section class="px-6" :class="[bgColor, verticalTopSpacing, verticalBottomSpacing]">
    <div class="mx-auto max-w-[80rem] lg:space-y-6">
      <BaseSectionHeader
        :title="sectionHeader.title"
        :tagline="sectionHeader.tagline"
        :alignment="sectionHeader.alignment"
        :paragraphs="sectionHeader.paragraphs"
        :primary-button="sectionHeader.primaryButton"
        :secondary-button="sectionHeader.secondaryButton"
        :variant="sectionHeader.variant"
        :section-intro="sectionHeader.sectionIntro"
        :is-centered="false"
      ></BaseSectionHeader>

      <ul class="hidden flex-wrap gap-4 lg:flex">
        <li v-for="category in categories" :key="category.id">
          <button
            class="rounded-md rounded-tl-none border px-4 py-1 text-xs font-bold text-text-dark transition-colors hover:border-accent-03 hover:bg-accent-03"
            :class="[
              selectedCategoryId == category.id
                ? 'border-accent-03 bg-accent-03'
                : 'border-border-light bg-white',
            ]"
            @click="toggleCategory(category.id)"
          >
            {{ category.title }}
          </button>
        </li>
      </ul>

      <ul class="hidden lg:block">
        <li v-for="question in filteredQuestions" :key="question.id">
          <BaseCollapse
            :id="question.id"
            :title="question.title"
            :text="question.text"
            :dark-mode="isDarkMode"
          ></BaseCollapse>
        </li>
      </ul>

      <!-- mobile -->
      <ul v-if="!selectedCategoryId" class="lg:hidden">
        <li
          v-for="category in categories"
          :key="category.id"
          class="border-b"
          :class="[
            isDarkMode ? 'border-white text-text-light' : 'border-border-light text-text-dark',
          ]"
        >
          <button
            class="flex w-full items-center justify-between py-4"
            @click="toggleCategory(category.id)"
          >
            <span class="text-xs font-bold"> {{ category.title }}</span>

            <BaseIcon class="size-6" :icon="'ArrowRight'"></BaseIcon>
          </button>
        </li>
      </ul>

      <!-- mobile -->
      <button
        v-if="selectedCategoryId"
        class="flex w-full items-center gap-2 border-b py-4 lg:hidden"
        :class="[
          isDarkMode ? 'border-white text-text-light' : 'border-border-light text-text-dark',
        ]"
        @click="toggleCategory(selectedCategoryId)"
      >
        <BaseIcon class="size-6" :icon="'ArrowLeft'"></BaseIcon>
        <p class="font-poppins text-display-lg font-bold">
          {{ selectedCategoryTitle }}
        </p>
      </button>

      <!-- mobile -->
      <ul v-if="selectedCategoryId" class="lg:hidden">
        <li v-for="question in filteredQuestions" :key="question.id">
          <BaseCollapse
            :id="question.id"
            :title="question.title"
            :text="question.text"
            :dark-mode="isDarkMode"
          ></BaseCollapse>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
import BaseSectionHeader from '@/common/components/base/BaseSectionHeader/BaseSectionHeader.vue'
import type { IBlockFaqExtended } from '@/features/block-faq-extended/types/IBlockFaqExtended.ts'
import { computed, ref } from 'vue'
import { getBgColor } from '@/common/utils/getBgColor.ts'
import BaseCollapse from '@/common/components/base/BaseCollapse/BaseCollapse.vue'
import BaseIcon from '@/common/components/base/BaseIcon/BaseIcon.vue'

const props = defineProps<IBlockFaqExtended>()

const selectedCategoryId = ref<string>()

const filteredQuestions = computed(() => {
  if (!selectedCategoryId.value) {
    return props.categories.flatMap(category => category.questions)
  }
  const selectedCategory = props.categories.find(cat => cat.id === selectedCategoryId.value)
  return selectedCategory ? selectedCategory.questions : []
})

const isDarkMode = computed(() => {
  return props.variant === 'blue'
})

const bgColor = computed(() => {
  return getBgColor(props.variant)
})

const selectedCategoryTitle = computed(() => {
  const selectedCategory = props.categories.find(cat => cat.id === selectedCategoryId.value)
  return selectedCategory ? selectedCategory.title : ''
})

const toggleCategory = (categoryId: string) => {
  if (selectedCategoryId.value === categoryId) {
    selectedCategoryId.value = undefined
  } else {
    selectedCategoryId.value = categoryId
  }
}
</script>
